import React, { useState, useEffect } from 'react';
import "../covfile/Install.css";
import { useAuth } from '../auth/Auth'; 

function App() {
  const { user } = useAuth(); 
  const [installationReports, setInstallationReports] = useState([]);
  const [groupedReports, setGroupedReports] = useState({});
  const [activeServerAddress, setActiveServerAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [archivedSessions, setArchivedSessions] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [isGeneralPoolCollapsed, setIsGeneralPoolCollapsed] = useState(false);
  const [isArchivePoolCollapsed, setIsArchivePoolCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [theme, setTheme] = useState('white'); // Додано стан для теми
  
  const userId = user?.user_id || localStorage.getItem('userId') || '271';

  // Функція для завантаження конфігурації теми
  const fetchThemeConfig = async () => {
    try {
      const response = await fetch(`https://hub2.daolog.net/user-theme-config/${userId}`);
      if (response.ok) {
        const config = await response.json();
        setTheme(config.theme || 'white');
        console.log('Theme loaded:', config.theme);
      } else {
        console.error('Failed to fetch theme config:', response.status);
        setTheme('white');
      }
    } catch (error) {
      console.error('Error fetching theme config:', error);
      setTheme('white');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Завантажуємо тему при завантаженні компонента
  useEffect(() => {
    fetchThemeConfig();
  }, [userId]);

  const handlePipToggle = () => {
    if (window.__interactiveTabPiP && window.__interactiveTabPiP.toggle) {
      window.__interactiveTabPiP.toggle({
        mode: 'element-only',
        targetSelector: '.pip-target-element' 
      });
    } else {
      alert('Для использования этой функции необходимо установить расширение "Interactive Tab PiP".');
    }
  };

  useEffect(() => {
    console.log('Current userId:', userId);
    console.log('Current user from auth:', user);
    console.log('localStorage userId:', localStorage.getItem('userId'));
  }, [user, userId]); 

  const refreshData = () => {
    setRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const initialLoad = async () => {
      if (installationReports.length === 0 && Object.keys(groupedReports).length === 0) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    };
    
    initialLoad();
  }, []);

  useEffect(() => {
    const fetchArchivedSessions = async () => {
      try {
        const response = await fetch(`https://hub2.daolog.net/api/archived-sessions/${userId}`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setArchivedSessions(new Set(data.archivedSessions));
          }
        }
      } catch (error) {
        console.error('Помилка завантаження архівованих сесій:', error);
      }
    };
    
    fetchArchivedSessions();
  }, [userId, refreshTrigger]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        console.log('No userId available, skipping data fetch');
        setLoading(false);
        return;
      }
  
      try {
        console.log('Fetching data for userId:', userId);
        
        setError('');
        
        const tokenResponse = await fetch(`https://hub2.daolog.net/api/user-token/${userId}`);
        
        if (!tokenResponse.ok) {
          throw new Error(`Помилка отримання токену: ${tokenResponse.status} ${tokenResponse.statusText}`);
        }
        
        const tokenData = await tokenResponse.json();
        const userToken = tokenData.token;
        
        if (!userToken) {
          throw new Error('Токен користувача не знайдено');
        }
        
        const response = await fetch(`https://hub2.daolog.net/api/user-installation-reports/${userId}`);
        
        if (!response.ok) {
          throw new Error(`Помилка отримання звітів: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        const reportsArray = data.reports || [];
        
        if (Array.isArray(reportsArray)) {
          const filteredReports = reportsArray.filter(report => report.session_id);
          
          const sortedData = [...filteredReports].sort((a, b) => {
            return new Date(b.timestamp) - new Date(a.timestamp);
          });
          
          setInstallationReports(sortedData);
          
          const grouped = sortedData.reduce((acc, report) => {
            const { session_id } = report;
            if (!session_id) return acc;
            
            if (!acc[session_id]) {
              acc[session_id] = [];
            }
            acc[session_id].push(report);
            return acc;
          }, {});
          
          setGroupedReports(grouped);
        } else {
          setError('Невірний формат даних від сервера');
        }
      } catch (error) {
        console.error('Помилка завантаження даних:', error);
        setError(`Помилка завантаження даних: ${error.message}`);
      } finally {
        setRefreshing(false);
      }
    };
    
    fetchData();
  }, [userId, refreshTrigger]);

  const archiveSession = async (sessionId, event) => {
    event.stopPropagation();
    
    try {
      const response = await fetch(`https://hub2.daolog.net/api/archive-session/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setArchivedSessions(new Set(data.archivedSessions));
        }
      }
    } catch (error) {
      console.error('Помилка архівування сесії:', error);
    }
  };

  const unarchiveSession = async (sessionId, event) => {
    event.stopPropagation();
    
    try {
      const response = await fetch(`https://hub2.daolog.net/api/unarchive-session/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setArchivedSessions(new Set(data.archivedSessions));
        }
      }
    } catch (error) {
      console.error('Помилка відновлення сесії:', error);
    }
  };

  const getLatestReport = (sessionReports) => {
    if (!sessionReports || sessionReports.length === 0) return null;
    return sessionReports[0];
  };

  const getServerAddressForSession = (sessionReports) => {
    if (!sessionReports || sessionReports.length === 0) return 'Невідомий сервер';
    return sessionReports[0].server_address;
  };

  const getInstallationStatus = (reports) => {
    if (!reports || reports.length === 0) return 'no_data';
    
    const latestReport = reports[0];
    
    if (latestReport.action === 'error') {
      return 'error';
    }
    
    if ((latestReport.action === 'install' || latestReport.action === 'install_office') && latestReport.success) {
      return 'completed';
    }
    
    if ((latestReport.action === 'install_started' || latestReport.action === 'install_office_started')) {
      return 'in_progress';
    }
    if (!latestReport.success && latestReport.error_message && latestReport.error_message.trim() !== '') {
      return 'error';
    }
    
    return 'unknown';
  };

  const getDisplayStatus = (reports) => {
    const status = getInstallationStatus(reports);
    
    switch (status) {
      case 'completed':
        return 'Успішно';
      case 'in_progress':
        return 'Встановлення...';
      case 'error':
        return 'Помилка';
      case 'no_data':
        return 'Немає даних';
      default:
        return 'Невідомо';
    }
  };

  const getInstallationStartTime = (reports) => {
    if (!reports || reports.length === 0) return null;
    
    for (let i = reports.length - 1; i >= 0; i--) {
      const report = reports[i];
      if ((report.action === 'install_started' || report.action === 'install_office_started')) {
        return report.timestamp;
      }
    }
    
    return null;
  };

  const Timer = ({ startTime, status }) => {
    const [currentTime, setCurrentTime] = useState(Date.now());
    
    useEffect(() => {
      let intervalId;
      
      if (status === 'in_progress' && startTime) {
        intervalId = setInterval(() => {
          setCurrentTime(Date.now());
        }, 1000);
      }
      
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [status, startTime]);
    
    if (status !== 'in_progress' || !startTime) return null;
    
    const startTimestamp = new Date(startTime).getTime();
    const timeDiff = currentTime - startTimestamp;
    const seconds = Math.floor(timeDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    return (
      <span className="server-timer">
        ({hours > 0 ? `${hours}г ` : ''}{minutes % 60}хв {seconds % 60}с)
      </span>
    );
  };

  const getAllSessions = () => {
    return Object.keys(groupedReports)
      .filter(sessionId => showArchived ? archivedSessions.has(sessionId) : !archivedSessions.has(sessionId))
      .map(sessionId => {
        const reports = groupedReports[sessionId];
        const latestReport = getLatestReport(reports);
        const serverAddress = getServerAddressForSession(reports);
        const status = getInstallationStatus(reports);
        const startTime = getInstallationStartTime(reports);
        
        return {
          sessionId,
          serverAddress,
          reports,
          latestReport,
          displayStatus: getDisplayStatus(reports),
          status,
          isSuccess: status === 'completed',
          isInProgress: status === 'in_progress',
          isError: status === 'error',
          startTime,
          latestTimestamp: latestReport ? new Date(latestReport.timestamp) : new Date(0),
          isArchived: archivedSessions.has(sessionId)
        };
      }).sort((a, b) => {
        return b.latestTimestamp - a.latestTimestamp;
      });
  };

  const [timerUpdate, setTimerUpdate] = useState(0);
  
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimerUpdate(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const createPipWindow = (serverAddress, sessionId) => {
    const pipWindow = window.open('', 'pipWindow', 
      'width=900,height=700,popup=yes,alwaysRaised=yes,alwaysOnTop=yes');
    
    if (!pipWindow) {
      alert('Спливаючі вікна заблоковані браузером. Будь ласка, дозвольте їх для цього сайту.');
      return;
    }
    
    const isDarkTheme = theme === 'dark';
    
    const style = pipWindow.document.createElement('style');
    style.textContent = `
      body {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        margin: 0;
        padding: 0;
        overflow-x: hidden;
        background-color: white;
      }
      
      .pip-container {
        width: 100%;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }
      
      .pip-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px;
        background-color: #282c34;
        color: white;
        position: sticky;
        top: 0;
        z-index: 100;
      }
      
      .pip-container.dark-theme {
        background-color: #1a202c;
        color: #e2e8f0;
      }
      
      .pip-container.dark-theme .pip-header {
        background-color: #1a202c;
      }
      
      .pip-header-content {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
      }
      
      .pip-header h3 {
        margin: 0;
        font-size: 16px;
      }
      
      .pip-header-buttons {
        display: flex;
        gap: 8px;
      }
      
      .download-button {
        background-color: #4CAF50;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: bold;
        font-size: 12px;
      }
      
      .pip-container.dark-theme .download-button {
        background-color: #48bb78;
      }
      
      .download-button:hover {
        background-color: #45a049;
      }
      
      .pip-container.dark-theme .download-button:hover {
        background-color: #38a169;
      }
      
      .exit-pip-button {
        background-color: #f44336;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: bold;
        font-size: 12px;
      }
      
      .pip-container.dark-theme .exit-pip-button {
        background-color: #f56565;
      }
      
      .exit-pip-button:hover {
        background-color: #d32f2f;
      }
      
      .pip-container.dark-theme .exit-pip-button:hover {
        background-color: #e53e3e;
      }
      
      .session-id {
        font-size: 11px;
        opacity: 0.8;
        background: rgba(255,255,255,0.1);
        padding: 2px 6px;
        border-radius: 3px;
      }
      
      .pip-container.dark-theme .session-id {
        color: #a0aec0;
      }
      
      .pip-content {
        flex: 1;
        padding: 10px;
        overflow-y: auto;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        background-color: white;
      }
      
      .pip-container.dark-theme table {
        background-color: #2d3748;
      }
      
      th, td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
        font-size: 12px;
      }
      
      .pip-container.dark-theme th,
      .pip-container.dark-theme td {
        border-color: #4a5568;
      }
      
      th {
        background-color: #f2f2f2;
        font-weight: bold;
        position: sticky;
        top: 0;
        z-index: 10;
      }
      
      .pip-container.dark-theme th {
        background-color: #414752;
        color: #ffffff;
      }
      
      tr:nth-child(even) {
        background-color: #f5f5f5;
      }
      
      .pip-container.dark-theme tr:nth-child(even) {
        background-color: #2d3c50;
      }
      
      tr:hover {
        background-color: #e9e9e9;
      }
      
      .pip-container.dark-theme tr:hover {
        background-color: #4a5568;
      }
      
      .success-true {
        color: green;
      }
      
      .pip-container.dark-theme .success-true {
        color: #68d391;
      }
      
      .success-false {
        color: red;
      }
      
      .pip-container.dark-theme .success-false {
        color: #fc8181;
      }
      
      .error-message {
        max-width: 300px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        hyphens: auto;
      }
      
      .pip-container.dark-theme .error-message {
        color: #fc8181;
        background-color: rgba(252, 129, 129, 0.1);
        padding: 4px;
        border-radius: 3px;
      }
      
      td {
        background-color: white;
        color: #333;
      }
      
      .pip-container.dark-theme td {
        background-color: #2d3748;
        color: #e2e8f0;
      }
      
      .pip-container.dark-theme tr:nth-child(even) td {
        background-color: #2d3c50;
      }
    `;
    pipWindow.document.head.appendChild(style);
    
    const serverData = groupedReports[sessionId] || [];
  
    const formatDateTime = (dateTimeString) => {
      const date = new Date(dateTimeString);
      return date.toLocaleString();
    };
    
    // Функція для завантаження файлу
    const downloadSessionFile = () => {
      const downloadUrl = `https://hub2.daolog.net/api/webhook/download/${sessionId}`;
      
      // Відкриваємо посилання в новому вікні для завантаження
      const downloadWindow = pipWindow.open(downloadUrl, '_blank');
      
      if (!downloadWindow) {
        pipWindow.alert('Помилка: не вдалося відкрити вікно для завантаження. Можливо, браузер заблокував спливаюче вікно.');
      }
    };
    
    const content = `
      <div class="pip-container ${isDarkTheme ? 'dark-theme' : ''}">
        <div class="pip-header">
          <div class="pip-header-content">
            <h3>Звіти встановлення для сервера: ${serverAddress}</h3>
            <span class="session-id">ID: ${sessionId}</span>
          </div>
          <div class="pip-header-buttons">
            <button class="download-button" id="download-button">
              📥 Завантажити лог
            </button>
            <button class="exit-pip-button" id="exit-button">
              Закрити
            </button>
          </div>
        </div>
        
        <div class="pip-content">
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>Версія</th>
                  <th>Тип встановлення</th>
                  <th>Статус</th>
                  <th>Дія</th>
                  <th>Час</th>
                  <th>Помилка</th>
                </tr>
              </thead>
              <tbody id="table-body">
                ${serverData.length > 0 ? 
                  serverData.map((report, index) => `
                    <tr>
                      <td>${report.version}</td>
                      <td>${report.install_type}</td>
                      <td class="success-${report.success}">${report.success ? 'Успішно' : 'Помилка'}</td>
                      <td>${report.action}</td>
                      <td>${formatDateTime(report.timestamp)}</td>
                      <td><div class="error-message">${report.error_message || '-'}</div></td>
                    </tr>
                  `).join('') : '<tr><td colspan="6">Немає даних</td></tr>'}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
    
    pipWindow.document.body.innerHTML = content;
    
    const exitButton = pipWindow.document.getElementById('exit-button');
    const downloadButton = pipWindow.document.getElementById('download-button');
    
    if (exitButton) {
      exitButton.addEventListener('click', () => {
        pipWindow.close();
      });
    }
    
    if (downloadButton) {
      downloadButton.addEventListener('click', downloadSessionFile);
    }
    
    pipWindow.focus();
  };

  const togglePipMode = (serverAddress, sessionId) => {
    createPipWindow(serverAddress, sessionId);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshData();
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const ServerCard = ({ session }) => {
    const { sessionId, serverAddress, reports, displayStatus, isSuccess, isInProgress, isError, startTime, isArchived } = session;
    
    const cardVariant = isSuccess ? 'completed' : isInProgress ? 'in-progress' : isError ? 'error' : 'default';
    
    return (
      <div className={`server-card ${cardVariant} ${isArchived ? 'archived' : ''}`}>
        <div className="server-content">
          <div className="server-info">
            <div className="server-address">{serverAddress || 'Невідомий сервер'}</div>
            <div className="server-status-row">
              <span className={`server-status ${isSuccess ? 'success' : isInProgress ? 'progress' : 'error'}`}>
                {displayStatus}
              </span>
              {isInProgress && startTime && (
                <Timer 
                  key={`${sessionId}-${timerUpdate}`}
                  startTime={startTime} 
                  status={isInProgress ? 'in_progress' : 'completed'} 
                />
              )}
            </div>
          </div>
          
          <div className="server-actions">
            {}
            {!isMobile && (
              <button 
                onClick={() => togglePipMode(serverAddress, sessionId)} 
                className="detail-button"
                title="Детальний перегляд"
              >
                →
              </button>
            )}
            {}
            {!showArchived && !isMobile && (
              <button 
                onClick={(e) => archiveSession(sessionId, e)}
                className="archive-button"
                title="Перемістити в архів"
              >
                Архів
              </button>
            )}
            {showArchived && !isMobile && (
              <button 
                onClick={(e) => unarchiveSession(sessionId, e)}
                className="unarchive-button"
                title="Відновити з архіву"
              >
                Відновити
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading && installationReports.length === 0) {
    return (
      <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <div className="App">
        <div className="main-container">
          <div className="single-column" style={{marginTop: "120px"}}>
            <div className="column-header">
              <h2>Встановлення BackOffice / Chain</h2>
              <div className="view-toggle">
                <button 
                  className={!showArchived ? 'active' : ''}
                  onClick={() => setShowArchived(false)}
                >
                  Поточні
                </button>
                <button 
                  className={showArchived ? 'active' : ''}
                  onClick={() => setShowArchived(true)}
                >
                  Архів
                </button>
              </div>
            </div>
            <div className="loading">
              {userId ? 'Завантаження даних...' : 'Очікування авторизації...'}
            </div>
          </div>
        </div>
      </div>
      </div>
    );
  }

  const activeSessions = getAllSessions().filter(s => !s.isArchived);
  const archivedSessionsList = getAllSessions().filter(s => s.isArchived);

  return (
    <div className="App">
         <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <div className="app-container">
        <div className="main-container">
          <div className="single-column">
            <div className="column-header">
              <div className="column-title">
                <h2>Встановлення BackOffice / Chain</h2>
              </div>
              <div className="column-actions">
                <div className="view-toggle">
                  <button 
                    className={!showArchived ? 'active green-button' : 'button'}
                    onClick={() => setShowArchived(false)}
                  >
                    Поточні ({activeSessions.length})
                  </button>
                  <button 
                    className={showArchived ? 'active' : 'button'}
                    onClick={() => setShowArchived(true)}
                  >
                    Архів ({archivedSessionsList.length})
                  </button>
                </div>
              </div>
            </div>
            
            {}
            {!showArchived ? (
              <div className="pool-section pip-target-element">
                <div className="pool-header">
                  <div className="pool-header-left">
                    <h2 className="pool-title">
                      Загальний пул
                      <span className="server-count">({activeSessions.length})</span>
                    </h2>
                  </div>
                  <button 
                    className="collapse-btn"
                    onClick={() => setIsGeneralPoolCollapsed(!isGeneralPoolCollapsed)}
                    aria-label={isGeneralPoolCollapsed ? "Розгорнути" : "Згорнути"}
                  >
                    {isGeneralPoolCollapsed ? '▼' : '▲'}
                  </button>
                </div>
                
                {!isGeneralPoolCollapsed && (
                  <div className="servers-list">
                    {activeSessions.length > 0 ? (
                      activeSessions.map((session) => (
                        <ServerCard key={session.sessionId} session={session} />
                      ))
                    ) : (
                      <div className="no-data">Немає активних серверів</div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="pool-section pip-target-element">
                <div className="pool-header">
                  <div className="pool-header-left">
                    <h2 className="pool-title">
                      Архів
                      <span className="server-count">({archivedSessionsList.length})</span>
                    </h2>
                  </div>
                  <button 
                    className="collapse-btn"
                    onClick={() => setIsArchivePoolCollapsed(!isArchivePoolCollapsed)}
                    aria-label={isArchivePoolCollapsed ? "Розгорнути" : "Згорнути"}
                  >
                    {isArchivePoolCollapsed ? '▼' : '▲'}
                  </button>
                </div>
                
                {!isArchivePoolCollapsed && (
                  <div className="servers-list">
                    {archivedSessionsList.length > 0 ? (
                      archivedSessionsList.map((session) => (
                        <ServerCard key={session.sessionId} session={session} />
                      ))
                    ) : (
                      <div className="no-data">Немає архівованих серверів</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {}
            <div className="pip-controls">
              <button 
                className="pip-toggle-btn"
                title="Открыть в режиме 'Картинка в картинке'"
                onClick={handlePipToggle}
              >
                📌 PiP Mode
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

export default App;